import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2,
  ArrowLeft,
  Users,
  Shirt,
  Wind,
  ShirtIcon,
  Droplets,
  Package,
  ListChecks,
  Truck,
  ShoppingBag,
  Settings,
  Cog,
  Grid3x3,
  Waves,
  DollarSign,
  FlaskConical,
  FileText,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import ParametrizacionInlineTable from './components/ParametrizacionInlineTable';
import AssignGarmentsModal from '@/pages/parametrizacion/clients/components/AssignGarmentsModal';
import GarmentPricesModal from '@/pages/parametrizacion/clients/components/GarmentPricesModal';
import ClientDocumentsModal from '@/pages/parametrizacion/clients/components/ClientDocumentsModal';
import VehicleDocumentsModal from '@/pages/parametrizacion/vehicles/components/VehicleDocumentsModal';
import ProgramProductsModal from '@/pages/parametrizacion/washingPrograms/components/ProgramProductsModal';

// Employees
import { employeesColumns, columnsExcel as employeesColumnsExcel } from '@/pages/parametrizacion/employees/utils/employeesColumns';
import { fetchEmployees, createEmployee, updateEmployee, deleteEmployee } from '@/pages/parametrizacion/employees/Services/employees.services';

// Machines
import { machinesColumns, columnsExcel as machinesColumnsExcel } from '@/pages/parametrizacion/machines/utils/machinesColumns';
import { fetchMachines, createMachine, updateMachine, deleteMachine } from '@/pages/parametrizacion/machines/Services/machines.services';

// Dryers
import { dryersColumns, columnsExcel as dryersColumnsExcel } from '@/pages/parametrizacion/dryers/utils/dryersColumns';
import { fetchDryers, createDryer, updateDryer, deleteDryer } from '@/pages/parametrizacion/dryers/Services/dryers.services';

// Garment Types
import { garmentTypesColumns, columnsExcel as garmentTypesColumnsExcel } from '@/pages/parametrizacion/garmentTypes/utils/garmentTypesColumns';
import { getGarmentTypes, createGarmentType, updateGarmentType, deleteGarmentType } from '@/pages/parametrizacion/garmentTypes/Services/garmentTypes.services';

// Garment Families
import { garmentFamiliesColumns, columnsExcel as garmentFamiliesColumnsExcel } from '@/pages/parametrizacion/garmentFamilies/utils/garmentFamiliesColumns';
import { getGarmentFamilies, createGarmentFamily, updateGarmentFamily, deleteGarmentFamily } from '@/pages/parametrizacion/garmentFamilies/Services/garmentFamilies.services';

// Client Garments
import { clientGarmentsColumns, columnsExcel as clientGarmentsColumnsExcel } from '@/pages/parametrizacion/clientGarments/utils/clientGarmentsColumns';
import { getClientGarments, createClientGarment, updateClientGarment, deleteClientGarment } from '@/pages/parametrizacion/clientGarments/Services/clientGarments.services';

// Washing Programs
import { washingProgramsColumns, columnsExcel as washingProgramsColumnsExcel } from '@/pages/parametrizacion/washingPrograms/utils/washingProgramsColumns';
import { getWashingPrograms, createWashingProgram, updateWashingProgram, deleteWashingProgram } from '@/pages/parametrizacion/washingPrograms/Services/washingPrograms.services';

// Products
import { productsColumns, columnsExcel as productsColumnsExcel } from '@/pages/parametrizacion/products/utils/productsColumns';
import { fetchProducts, createProduct, updateProduct, deleteProduct } from '@/pages/parametrizacion/products/Services/products.services';

// Services
import { servicesColumns, columnsExcel as servicesColumnsExcel } from '@/pages/parametrizacion/services/utils/servicesColumns';
import { fetchServices, createService, updateService, deleteService } from '@/pages/parametrizacion/services/Services/services.services';

// Vehicles
import { vehiclesColumns, columnsExcel as vehiclesColumnsExcel } from '@/pages/parametrizacion/vehicles/utils/vehiclesColumns';
import { fetchVehicles, createVehicle, updateVehicle, deleteVehicle } from '@/pages/parametrizacion/vehicles/Services/vehicles.services';

// Clients
import { clientsColumns, columnsExcel as clientsColumnsExcel } from '@/pages/parametrizacion/clients/utils/clientsColumns';
import { fetchClients, createClient, updateClient, deleteClient } from '@/pages/parametrizacion/clients/Services/clients.services';

// Users
import { usersColumns, columnsExcel as usersColumnsExcel } from '@/pages/parametrizacion/usuarios/utils/usersColumns';
import { fetchUsers, createUser, updateUser, deleteUser } from '@/pages/parametrizacion/usuarios/Services/usuarios.services';

const CentroParametrizacionPage = () => {
  const { idLavanderia } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [laundry, setLaundry] = useState(location.state?.laundry || null);
  const [selectedModuleId, setSelectedModuleId] = useState(null);
  // Colapsa la sidebar de módulos para dar más espacio al contenido principal
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Modals for client module (assign garments, prices & documents)
  const [assignGarmentsOpen, setAssignGarmentsOpen] = useState(false);
  const [garmentPricesOpen, setGarmentPricesOpen] = useState(false);
  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
  const [vehicleDocumentsOpen, setVehicleDocumentsOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  // Modal for washing program products
  const [programProductsOpen, setProgramProductsOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);

  // Lavanderías para inyectar opciones en filtros (ej. usuarios)
  const [laundryOptions, setLaundryOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { fetchLaundries } = await import('@/pages/parametrizacion/laundries/Services/laundries.services');
        const r = await fetchLaundries({ limit: 200, offset: 1, query: { estado: 'Activo' } });
        const opts = (r.data || []).map(l => ({ value: l._id, label: l.nombre }));
        if (!cancelled) setLaundryOptions(opts);
      } catch (e) {
        console.error('Error precargando lavanderías', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!laundry && idLavanderia) {
      // Opcional: cargar datos del centro si no vienen por state
    }
  }, [idLavanderia, laundry]);

  // Helper: normaliza columnas de exportación al formato { header, key }
  const normalizeExportColumns = (cols) => {
    if (!cols) return [];
    // Si es un objeto plano { key: header, ... } (ej: washingPrograms)
    if (!Array.isArray(cols)) {
      return Object.entries(cols).map(([key, header]) => ({ key, header }));
    }
    // Si ya es un array, devolver tal cual
    return cols;
  };

  // Helper: envuelve fetchService para exportar (sin paginación)
  const buildExportService = (fetchSvc) => async ({ query, limit, page }) => {
    const response = await fetchSvc({ query, page, limit });
    return response?.data || [];
  };

  const moduleGroups = [
    {
      title: 'Registros Diarios / Operación',
      items: [
        {
          id: 'lavadoras',
          title: 'Lavadoras',
          description: 'Máquinas de lavado',
          icon: Waves,
          color: 'text-cyan-600',
          bgColor: 'bg-cyan-50',
          activeBg: 'bg-cyan-50 border-cyan-300',
          fetchService: fetchMachines,
          createService: createMachine,
          updateService: updateMachine,
          deleteService: deleteMachine,
          formFields: machinesColumns,
          tableColumns: [
            { id: 'nombre', label: 'Nombre', searchable: true },
            { id: 'capacidad', label: 'Capacidad (kg)' },
            { id: 'contador', label: 'Contador' },
            { id: 'estado', label: 'Estado', type: 'status' },
          ],
          filterFields: [
            {
              id: 'estado', label: 'Estado', type: 'select', options: [
                { value: 'Activa', label: 'Activa' },
                { value: 'Inactiva', label: 'Inactiva' },
              ]
            },
          ],
          exportColumns: normalizeExportColumns(machinesColumnsExcel),
        },
        {
          id: 'secadoras',
          title: 'Secadoras',
          description: 'Máquinas de secado',
          icon: Wind,
          color: 'text-sky-600',
          bgColor: 'bg-sky-50',
          activeBg: 'bg-sky-50 border-sky-300',
          fetchService: fetchDryers,
          createService: createDryer,
          updateService: updateDryer,
          deleteService: deleteDryer,
          formFields: dryersColumns,
          tableColumns: [
            { id: 'nombre', label: 'Nombre', searchable: true },
            { id: 'capacidad', label: 'Capacidad (kg)' },
            { id: 'contador', label: 'Contador' },
            { id: 'estado', label: 'Estado', type: 'status' },
          ],
          filterFields: [
            {
              id: 'estado', label: 'Estado', type: 'select', options: [
                { value: 'Activa', label: 'Activa' },
                { value: 'Inactiva', label: 'Inactiva' },
              ]
            },
          ],
          exportColumns: normalizeExportColumns(dryersColumnsExcel),
        },

        {
          id: 'servicios',
          title: 'Servicios',
          description: 'Servicios ofrecidos',
          icon: ListChecks,
          color: 'text-amber-600',
          bgColor: 'bg-amber-50',
          activeBg: 'bg-amber-50 border-amber-300',
          fetchService: fetchServices,
          createService: createService,
          updateService: updateService,
          deleteService: deleteService,
          formFields: servicesColumns,
          tableColumns: [
            { id: 'nombre', label: 'Nombre', searchable: true },
            { id: 'precio', label: 'Precio' },
            { id: 'unidad', label: 'Unidad' },
            { id: 'estado', label: 'Estado', type: 'status' },
          ],
          filterFields: [
            {
              id: 'estado', label: 'Estado', type: 'select', options: [
                { value: 'Activo', label: 'Activo' },
                { value: 'Inactivo', label: 'Inactivo' },
              ]
            },
          ],
          exportColumns: normalizeExportColumns(servicesColumnsExcel),
        },
      ],
    },
    {
      title: 'Operación',
      items: [
        {
          id: 'empleados',
          title: 'Empleados',
          description: 'Personal del centro',
          icon: Users,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          activeBg: 'bg-blue-50 border-blue-300',
          fetchService: fetchEmployees,
          createService: createEmployee,
          updateService: updateEmployee,
          deleteService: deleteEmployee,
          formFields: employeesColumns,
          tableColumns: [
            { id: 'nombre', label: 'Nombre', searchable: true },
            { id: 'apellido', label: 'Apellido' },
            { id: 'rol', label: 'Rol' },
            { id: 'estado', label: 'Estado', type: 'status' },
          ],
          filterFields: [
            {
              id: 'rol', label: 'Rol', type: 'select', options: [
                { value: 'Admin', label: 'Administrador' },
                { value: 'Conductor', label: 'Conductor' },
                { value: 'Supervisor planta', label: 'Supervisor' },
                { value: 'Auxiliar lavanderia', label: 'Auxiliar' },
              ]
            },
            {
              id: 'estado', label: 'Estado', type: 'select', options: [
                { value: 'Activo', label: 'Activo' },
                { value: 'Inactivo', label: 'Inactivo' },
              ]
            },
          ],
          exportColumns: normalizeExportColumns(employeesColumnsExcel),
        },
        {
          id: 'programas-lavado',
          title: 'Programas',
          description: 'Programas de lavado',
          icon: Droplets,
          color: 'text-teal-600',
          bgColor: 'bg-teal-50',
          activeBg: 'bg-teal-50 border-teal-300',
          fetchService: getWashingPrograms,
          createService: createWashingProgram,
          updateService: updateWashingProgram,
          deleteService: deleteWashingProgram,
          formFields: washingProgramsColumns,
          tableColumns: [
            { id: 'nombre', label: 'Nombre', searchable: true },
            { id: 'nivel_suciedad', label: 'Nivel de suciedad' },
            { id: 'productos', label: 'Productos', render: (row) => {
              const productos = Array.isArray(row.productos) ? row.productos : [];
              const count = productos.length;
              const totalMl = productos.reduce((sum, p) => sum + (Number(p.cantidadMililitros) || 0), 0);
              return (
                <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                  {count} prod • {totalMl}ml
                </span>
              );
            }},
            { id: 'estado', label: 'Estado', type: 'status' },
          ],
          filterFields: [
            {
              id: 'estado', label: 'Estado', type: 'select', options: [
                { value: 'Activo', label: 'Activo' },
                { value: 'Inactivo', label: 'Inactivo' },
              ]
            },
          ],
          extraActions: [
            {
              icon: FlaskConical,
              label: 'Gestionar Productos',
              className: 'text-blue-600 hover:text-blue-700',
              onClick: (row) => {
                setSelectedProgram(row);
                setProgramProductsOpen(true);
              },
            },
          ],
          exportColumns: normalizeExportColumns(washingProgramsColumnsExcel),
        },
        {
          id: 'tipos-prenda',
          title: 'Prendas de lavado',
          description: 'Prendas recepción de ropa sucia',
          icon: Shirt,
          color: 'text-violet-600',
          bgColor: 'bg-violet-50',
          activeBg: 'bg-violet-50 border-violet-300',
          fetchService: getGarmentTypes,
          createService: createGarmentType,
          updateService: updateGarmentType,
          deleteService: deleteGarmentType,
          formFields: garmentTypesColumns,
          tableColumns: [
            { id: 'nombre', label: 'Nombre', searchable: true },
            { id: 'peso', label: 'Peso', accessor: 'peso' },
            { id: 'estado', label: 'Estado', type: 'status' },
          ],
          filterFields: [
            {
              id: 'estado', label: 'Estado', type: 'select', options: [
                { value: 'Activo', label: 'Activo' },
                { value: 'Inactivo', label: 'Inactivo' },
              ]
            },
          ],
          exportColumns: normalizeExportColumns(garmentTypesColumnsExcel),
        },
        {
          id: 'clientes',
          title: 'Clientes',
          description: 'Clientes asignados',
          icon: ShoppingBag,
          color: 'text-rose-600',
          bgColor: 'bg-rose-50',
          activeBg: 'bg-rose-50 border-rose-300',
          fetchService: fetchClients,
          createService: createClient,
          updateService: updateClient,
          deleteService: deleteClient,
          formFields: clientsColumns,
          tableColumns: [
            { id: 'nombre', label: 'Nombre', searchable: true },
            { id: 'nit', label: 'NIT' },
            { id: 'tipoCobro', label: 'Tipo Cobro' },
            { id: 'estado', label: 'Estado', type: 'status' },
          ],
          filterFields: [
            {
              id: 'estado', label: 'Estado', type: 'select', options: [
                { value: 'Activo', label: 'Activo' },
                { value: 'Inactivo', label: 'Inactivo' },
              ]
            },
          ],
          extraActions: [
            {
              icon: Shirt,
              label: 'Asignar Prendas',
              className: 'text-blue-600 hover:text-blue-700',
              onClick: (row) => {
                setSelectedClient(row);
                setAssignGarmentsOpen(true);
              },
            },
            {
              icon: DollarSign,
              label: 'Precios por Prenda',
              className: 'text-green-600 hover:text-green-700',
              onClick: (row) => {
                setSelectedClient(row);
                setGarmentPricesOpen(true);
              },
            },
            {
              icon: FileText,
              label: 'Adjuntos',
              className: 'text-purple-600 hover:text-purple-700',
              onClick: (row) => {
                setSelectedClient(row);
                setDocumentsModalOpen(true);
              },
            },
          ],
          exportColumns: normalizeExportColumns(clientsColumnsExcel),
        },
      ],
    },
    {
      title: 'Generales',
      items: [

        {
          id: 'familia-prendas',
          title: 'Familias',
          description: 'Familias de prendas',
          icon: Grid3x3,
          color: 'text-purple-600',
          bgColor: 'bg-purple-50',
          activeBg: 'bg-purple-50 border-purple-300',
          scope: 'global',
          fetchService: getGarmentFamilies,
          createService: createGarmentFamily,
          updateService: updateGarmentFamily,
          deleteService: deleteGarmentFamily,
          formFields: garmentFamiliesColumns,
          tableColumns: [
            { id: 'nombre', label: 'Nombre', searchable: true },
            { id: 'descripcion', label: 'Descripción' },
            { id: 'estado', label: 'Estado', type: 'status' },
          ],
          filterFields: [
            {
              id: 'estado', label: 'Estado', type: 'select', options: [
                { value: 'Activo', label: 'Activo' },
                { value: 'Inactivo', label: 'Inactivo' },
              ]
            },
          ],
          exportColumns: normalizeExportColumns(garmentFamiliesColumnsExcel),
        },
        {
          id: 'prendas-clientes',
          title: 'Prendas alistamiento',
          description: 'Catálogo de prendas registradas por cliente',
          icon: ShirtIcon,
          color: 'text-fuchsia-600',
          bgColor: 'bg-fuchsia-50',
          activeBg: 'bg-fuchsia-50 border-fuchsia-300',
          scope: 'global',
          fetchService: getClientGarments,
          createService: createClientGarment,
          updateService: updateClientGarment,
          deleteService: deleteClientGarment,
          formFields: clientGarmentsColumns,
          tableColumns: [
            { id: 'nombre', label: 'Nombre', searchable: true },
            { id: 'peso', label: 'Peso (g)' },
            { id: 'areaAlistamiento', label: 'Área' },
            { id: 'familia', label: 'Familia', accessor: 'familia.nombre' },
            { id: 'estado', label: 'Estado' },
          ],
          filterFields: [],
          exportColumns: normalizeExportColumns(clientGarmentsColumnsExcel),
        },
        {
          id: 'productos',
          title: 'Productos',
          description: 'Insumos y materiales',
          icon: Package,
          color: 'text-emerald-600',
          bgColor: 'bg-emerald-50',
          activeBg: 'bg-emerald-50 border-emerald-300',
          scope: 'global',
          fetchService: fetchProducts,
          createService: createProduct,
          updateService: updateProduct,
          deleteService: deleteProduct,
          formFields: productsColumns,
          tableColumns: [
            { id: 'nombre', label: 'Nombre', searchable: true },
            { id: 'proveedor', label: 'Proveedor', accessor: 'proveedor.nombre' },
            { id: 'unidad', label: 'Unidad' },
            { id: 'presentacionLitros', label: 'Presentación (L)' },
            { id: 'costo', label: 'Costo' },
            { id: 'stockMinimo', label: 'Stock Mín.' },
            { id: 'estado', label: 'Estado', type: 'status' },
          ],
          filterFields: [
            {
              id: 'estado', label: 'Estado', type: 'select', options: [
                { value: 'Activo', label: 'Activo' },
                { value: 'Inactivo', label: 'Inactivo' },
              ]
            },
          ],
          exportColumns: normalizeExportColumns(productsColumnsExcel),
        },
        {
          id: 'vehiculos',
          title: 'Vehículos',
          description: 'Transporte del centro',
          icon: Truck,
          color: 'text-orange-600',
          bgColor: 'bg-orange-50',
          activeBg: 'bg-orange-50 border-orange-300',
          scope: 'global',
          fetchService: fetchVehicles,
          createService: createVehicle,
          updateService: updateVehicle,
          deleteService: deleteVehicle,
          formFields: vehiclesColumns,
          tableColumns: [
            { id: 'placa', label: 'Placa', searchable: true },
            { id: 'nombre', label: 'Nombre' },
            { id: 'marca', label: 'Marca' },
            { id: 'kilometraje', label: 'Kilometraje' },
            { id: 'estado', label: 'Estado', type: 'status' },
          ],
          extraActions: [
            {
              icon: FileText,
              label: 'Adjuntos',
              className: 'text-purple-600 hover:text-purple-700',
              onClick: (row) => {
                setSelectedVehicle(row);
                setVehicleDocumentsOpen(true);
              },
            },
          ],
          filterFields: [
            {
              id: 'estado', label: 'Estado', type: 'select', options: [
                { value: 'Activo', label: 'Activo' },
                { value: 'Inactivo', label: 'Inactivo' },
              ]
            },
          ],
          exportColumns: normalizeExportColumns(vehiclesColumnsExcel),
        },
        {
          id: 'usuarios',
          title: 'Usuarios',
          description: 'Usuarios del sistema',
          icon: Settings,
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          activeBg: 'bg-gray-50 border-gray-300',
          scope: 'global',
          fetchService: fetchUsers,
          createService: createUser,
          updateService: updateUser,
          deleteService: deleteUser,
          // Usamos la misma definición canónica que /parametrizacion/usuarios
          // para garantizar que rol/lavanderia/cedula/contrasena coincidan
          // con el schema del backend (User.rol, User.lavanderia, User.cedula,
          // User.contrasena) y con el módulo de parametrización de usuarios.
          formFields: usersColumns,
          tableColumns: [
            { id: 'nombre', label: 'Nombre', searchable: true },
            { id: 'correo', label: 'Email' },
            { id: 'rol', label: 'Rol' },
            {
              id: 'lavanderia',
              label: 'Centro de Lavado',
              accessor: 'lavanderia.nombre',
            },
            { id: 'estado', label: 'Estado', type: 'status' },
          ],
          filterFields: [
            // Filtro de Rol: reusamos el mismo set de opciones del form
            // para evitar duplicación y drift.
            {
              ...usersColumns.find((c) => c.id === 'rol'),
              id: 'rol',
            },
            // Filtro de Centro de Lavado: el backend espera `lavanderia.id`
            {
              id: 'lavanderia.id',
              label: 'Centro de Lavado',
              type: 'select',
              options: laundryOptions,
            },
            // Filtro de estado
            usersColumns.find((c) => c.id === 'estado'),
          ].filter(Boolean),
          exportColumns: normalizeExportColumns(usersColumnsExcel),
        },
      ],
    },
  ];

  const allModules = moduleGroups.flatMap((g) => g.items);
  const selectedModule = allModules.find((m) => m.id === selectedModuleId);

  if (!laundry) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-12 text-center">
            <Building2 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700">Centro no encontrado</h3>
            <p className="text-sm text-gray-500 mt-2">Selecciona un centro desde el listado</p>
            <Button onClick={() => navigate('/parametrizacion/centros')} className="mt-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver al listado
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-4">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/parametrizacion/centros')}
                className="h-9 w-9"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="p-2 bg-indigo-100 rounded-xl">
                <Building2 className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{laundry.nombre}</h1>
                <p className="text-xs text-gray-500">Parametrización del centro</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Two column layout */}
      <div className="flex flex-col lg:flex-row gap-4 min-h-[600px]">
        {/* Left sidebar - Module list */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className={`w-full shrink-0 transition-all duration-300 ${
            sidebarCollapsed ? 'lg:w-14' : 'lg:w-72'
          }`}
        >
          <Card className="h-full">
            <CardContent className={`p-3 relative ${sidebarCollapsed ? 'px-1' : ''}`}>
              <div className="flex items-center justify-between mb-3 px-1">
                {!sidebarCollapsed && (
                  <h2 className="text-sm font-semibold text-gray-700">
                    Módulos de Parametrización
                  </h2>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarCollapsed(prev => !prev)}
                  title={sidebarCollapsed ? 'Expandir módulos' : 'Ocultar módulos'}
                  className={`h-9 w-9 ${sidebarCollapsed ? 'mx-auto' : ''}`}
                >
                  {sidebarCollapsed ? (
                    <ChevronRight className="h-4 w-4 text-gray-600" />
                  ) : (
                    <ChevronLeft className="h-4 w-4 text-gray-600" />
                  )}
                </Button>
              </div>
              <div className="space-y-4">
                {moduleGroups.map((group) => (
                  <div key={group.title}>
                    {!sidebarCollapsed && (
                      <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-1 mb-1.5">
                        {group.title}
                      </h3>
                    )}
                    <div className="space-y-0.5">
                      {group.items.map((module) => {
                        const Icon = module.icon;
                        const isActive = selectedModuleId === module.id;
                        if (sidebarCollapsed) {
                          // Vista colapsada: solo icono centrado con tooltip
                          return (
                            <button
                              key={module.id}
                              onClick={() => setSelectedModuleId(module.id)}
                              title={module.title}
                              className={`w-full flex items-center justify-center p-2 rounded-lg transition-all ${
                                isActive
                                  ? `${module.activeBg} shadow-sm`
                                  : 'hover:bg-gray-50 text-gray-600'
                              }`}
                            >
                              <div className={`p-1.5 rounded-md ${isActive ? module.bgColor : 'bg-gray-100'}`}>
                                <Icon className={`h-4 w-4 ${isActive ? module.color : 'text-gray-500'}`} />
                              </div>
                            </button>
                          );
                        }
                        return (
                          <button
                            key={module.id}
                            onClick={() => setSelectedModuleId(module.id)}
                            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm transition-all ${isActive
                              ? `${module.activeBg} font-semibold shadow-sm`
                              : 'hover:bg-gray-50 text-gray-600'
                              }`}
                          >
                            <div className={`p-1.5 rounded-md ${isActive ? module.bgColor : 'bg-gray-100'}`}>
                              <Icon className={`h-4 w-4 ${isActive ? module.color : 'text-gray-500'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${isActive ? 'text-gray-900' : 'text-gray-700'}`}>
                                {module.title}
                              </p>
                              <p className="text-[11px] text-gray-400 truncate">{module.description}</p>
                            </div>
                            {isActive && (
                              <div className={`w-1.5 h-1.5 rounded-full ${module.color.replace('text-', 'bg-')}`} />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Right content area */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="flex-1 min-w-0"
        >
          <Card className="h-full">
            <CardContent className="p-5">
              {selectedModule ? (
                <div key={selectedModule.id}>
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b">
                    <selectedModule.icon className={`h-5 w-5 ${selectedModule.color}`} />
                    <h2 className="text-lg font-semibold text-gray-900">
                      {selectedModule.title}
                    </h2>
                  </div>
                  <ParametrizacionInlineTable
                    title={selectedModule.title}
                    idLavanderia={idLavanderia}
                    scope={selectedModule.scope || 'centro'}
                    fetchService={selectedModule.fetchService}
                    createService={selectedModule.createService}
                    updateService={selectedModule.updateService}
                    deleteService={selectedModule.deleteService}
                    formFields={selectedModule.formFields}
                    columns={selectedModule.tableColumns}
                    filterFields={selectedModule.filterFields}
                    extraActions={selectedModule.extraActions || []}
                    exportService={buildExportService(selectedModule.fetchService)}
                    exportColumns={selectedModule.exportColumns || []}
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="p-4 bg-gray-100 rounded-full mb-4">
                    <Cog className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-700">
                    Selecciona una parametrización
                  </h3>
                  <p className="text-sm text-gray-500 mt-1 max-w-sm">
                    Elige un módulo del menú lateral para ver y gestionar la parametrización de este centro de lavado.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Client extra modals */}
      <AssignGarmentsModal
        isOpen={assignGarmentsOpen}
        onClose={() => { setAssignGarmentsOpen(false); setSelectedClient(null); }}
        client={selectedClient}
        onSuccess={() => { setAssignGarmentsOpen(false); setSelectedClient(null); }}
      />
      <GarmentPricesModal
        isOpen={garmentPricesOpen}
        onClose={() => { setGarmentPricesOpen(false); setSelectedClient(null); }}
        client={selectedClient}
        onSuccess={() => { setGarmentPricesOpen(false); setSelectedClient(null); }}
      />
      <ClientDocumentsModal
        isOpen={documentsModalOpen}
        onClose={() => { setDocumentsModalOpen(false); setSelectedClient(null); }}
        client={selectedClient}
      />
      <VehicleDocumentsModal
        isOpen={vehicleDocumentsOpen}
        onClose={() => { setVehicleDocumentsOpen(false); setSelectedVehicle(null); }}
        vehicle={selectedVehicle}
      />

      {/* Washing Program Products Modal */}
      <ProgramProductsModal
        isOpen={programProductsOpen}
        onClose={() => { setProgramProductsOpen(false); setSelectedProgram(null); }}
        program={selectedProgram}
        onSuccess={() => {
          setProgramProductsOpen(false);
          setSelectedProgram(null);
          // Refresh the inline table data
          const module = allModules.find(m => m.id === selectedModuleId);
          if (module?.fetchService) {
            // The table will auto-refresh on next interaction or we can trigger a reload
          }
        }}
      />
    </div>
  );
};

export default CentroParametrizacionPage;
